"use client";

import { Loading } from "@/components/ui/loading";
import { Profile } from "../../talent/types";
import ApplicationOrCandidateProfileList from "@/features/common/ApplicationOrCandidateProfileList";
import { DeleteUserDialog } from "@/features/common/DeleteUserDialog";
import { useTranslation } from "react-i18next";
import { useState } from "react";

interface BaseTalentsListProps {
  talentProfiles: Profile[];
  loading: boolean;
  error: string | null;
}

export default function BaseTalentsList({
  talentProfiles,
  loading,
  error
}: BaseTalentsListProps) {
  const { t } = useTranslation(["applications"]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<Profile | null>(null);

  const handleExport = (data: Profile[]) => {
    const csvContent = [
      // Headers
      [
        t("columns.name"),
        t("columns.qualification"),
        t("columns.german_language_skills"),
        t("columns.german_drivers_license"),
        t("columns.nationality"),
        t("columns.gender"),
        t("columns.date_of_birth"),
        t("columns.address"),
        t("columns.postal_code"),
        t("columns.city"),
        t("columns.state"),
        t("columns.country"),
        t("columns.relationship_status"),
        t("columns.criminal_record"),
        t("columns.nursing_education_status"),
        t("columns.recognition_status"),
        t("columns.theory_hours_deficit"),
        t("columns.practice_hours_deficit"),
        t("columns.visa_application_status"),
        t("columns.labor_market_authorization_status"),
        t("columns.health_insurance"),
        t("columns.travel_health_insurance"),
        t("columns.work_experience"),
        t("columns.advanced_training"),
        t("columns.drivers_license"),
        t("columns.facility_preferences"),
        t("columns.location_preferences"),
        t("columns.position_preferences"),
        t("columns.working_hours"),
        t("columns.shift_model"),
        t("columns.earliest_start_date"),
        t("columns.preparation_course_enrolled"),
        t("columns.preparation_course_name"),
        t("columns.preparation_course_provider"),
        t("columns.preparation_course_start_date"),
        t("columns.preparation_course_end_date"),
        t("columns.preparation_exam_date"),
        t("columns.preparation_exam_location"),
        t("columns.preparation_pre_exam"),
        t("columns.created_at"),
        t("columns.updated_at")
      ],
      // Data rows
      ...data.map((profile) => [
        `${profile.given_name || ""} ${profile.family_name || ""}`.trim(),
        profile.nursing_education_status === "completed"
          ? t("status_labels.nursing_education.completed")
          : t("status_labels.nursing_education.not_started"),
        profile.german_language_skills || "-",
        profile.german_drivers_license &&
        profile.german_drivers_license.length > 0
          ? profile.german_drivers_license.join(", ")
          : "-",
        profile.nationality || "-",
        profile.gender || "-",
        profile.date_of_birth
          ? new Date(profile.date_of_birth).toLocaleDateString("de-DE")
          : "-",
        profile.address_1 || "-",
        profile.postal_code || "-",
        profile.city || "-",
        profile.state || "-",
        profile.country || "-",
        profile.relationship_status || "-",
        profile.criminal_record || "-",
        profile.nursing_education_status || "-",
        profile.recognition_status || "-",
        profile.theory_hours_deficit || "-",
        profile.practice_hours_deficit || "-",
        profile.visa_application_status || "-",
        profile.labor_market_authorization_status || "-",
        profile.health_insurance || "-",
        profile.travel_health_insurance || "-",
        profile.work_experience || "-",
        profile.advanced_training || "-",
        profile.drivers_license && profile.drivers_license.length > 0
          ? profile.drivers_license.join(", ")
          : "-",
        profile.facility_preferences && profile.facility_preferences.length > 0
          ? profile.facility_preferences.join(", ")
          : "-",
        profile.location_preferences && profile.location_preferences.length > 0
          ? profile.location_preferences.join(", ")
          : "-",
        profile.position_preferences && profile.position_preferences.length > 0
          ? profile.position_preferences.join(", ")
          : "-",
        profile.working_hours || "-",
        profile.shift_model || "-",
        profile.earliest_start_date
          ? new Date(profile.earliest_start_date).toLocaleDateString("de-DE")
          : "-",
        profile.preparation_course_enrolled
          ? t("status_labels.boolean.yes")
          : t("status_labels.boolean.no"),
        profile.preparation_course_name || "-",
        profile.preparation_course_provider || "-",
        profile.preparation_course_start_date
          ? new Date(profile.preparation_course_start_date).toLocaleDateString(
              "de-DE"
            )
          : "-",
        profile.preparation_course_end_date
          ? new Date(profile.preparation_course_end_date).toLocaleDateString(
              "de-DE"
            )
          : "-",
        profile.preparation_exam_date
          ? new Date(profile.preparation_exam_date).toLocaleDateString("de-DE")
          : "-",
        profile.preparation_exam_location || "-",
        profile.preparation_pre_exam
          ? t("status_labels.boolean.yes")
          : t("status_labels.boolean.no")
      ])
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `talent_profiles_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = (id: string) => {
    const profile = talentProfiles.find((p) => p.talent_id === id);
    if (profile) {
      setProfileToDelete(profile);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirmed = () => {
    // Refetch is handled by DeleteUserDialog's onDeleted callback
    // We just need to update local state
    setProfileToDelete(null);
  };

  if (loading) return <Loading text={t("messages.loading_talent_profiles")} />;

  if (error)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-red-500">
          {t("messages.error")}: {error}
        </div>
      </div>
    );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <ApplicationOrCandidateProfileList
        applicationsOrProfiles={talentProfiles}
        onExport={handleExport}
        onDelete={handleDelete}
        isApplicationList={false}
      />
      {profileToDelete && (
        <DeleteUserDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setProfileToDelete(null);
          }}
          userId={profileToDelete.talent_id || ""}
          userName={`${profileToDelete.given_name || ""} ${profileToDelete.family_name || ""}`.trim()}
          onDeleted={handleDeleteConfirmed}
        />
      )}
    </div>
  );
}
